import { Box, InputAdornment, TextField } from '@mui/material';

import { PromocodeListItemFragment, PromocodeStatus } from '@occc/graphql';
import {
  TrendBadge,
  CircularProgress,
  EmptyDataTable,
  InfoDialog,
  IPaginationProps,
  LoadingOverlay,
  Pagination,
  SearchIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  Typography,
} from '@occc/ui-design-system';
import { DateTimeFormat, formatDateTime, parseDate } from '@occc-app/lib/date';

import { messages } from '../../promocodes.messages';

const getStatusColor = (status: PromocodeStatus) => {
  if (status === PromocodeStatus.Redeemed) {
    return 'green';
  }
  if (status === PromocodeStatus.Archived) {
    return 'red';
  }
  return 'black';
};

const getStatusLabel = (status: PromocodeStatus) => {
  if (status === PromocodeStatus.Active) {
    return 'Not Used';
  }

  return `${status.charAt(0)}${status.slice(1).toLowerCase()}`;
};

const TABLE_MAX_HEIGHT = '55vh';
const stickyHeaderCellSx = { backgroundColor: 'background.paper' };

type PromocodesIndividualViewAllPopupProps = {
  isOpen: boolean;
  onClose: () => void;
  search: string;
  onSearchChange: (value: string) => void;
  codes: PromocodeListItemFragment[];
  isLoading: boolean;
  hasError: boolean;
  paginationProps: IPaginationProps;
};

export const PromocodesIndividualViewAllPopup = ({
  isOpen,
  onClose,
  search,
  onSearchChange,
  codes,
  isLoading,
  hasError,
  paginationProps,
}: PromocodesIndividualViewAllPopupProps) => {
  const trimmedSearch = search.trim();
  const showEmpty = !isLoading && !hasError && codes.length === 0 && trimmedSearch.length === 0;
  const showNoResults = !isLoading && !hasError && codes.length === 0 && trimmedSearch.length > 0;

  return (
    <InfoDialog
      dialogTitle={messages.viewAllCodesTitle}
      open={isOpen}
      onClose={onClose}
      fullWidth
      maxWidth='sm'
    >
      <TableContainer variant='outlined'>
        <TableHeader
          actions={
            <TextField
              size='small'
              placeholder={messages.viewAllCodesSearchPlaceholder}
              value={search}
              onChange={(event) => onSearchChange(event.target.value)}
              sx={{ width: { xs: '100%', sm: '35%' } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon fontSize='small' />
                  </InputAdornment>
                ),
              }}
            />
          }
        />

        {isLoading && codes.length === 0 && !hasError && (
          <Box display='flex' justifyContent='center' py={6}>
            <CircularProgress />
          </Box>
        )}

        {hasError && (
          <Box py={6} color='error.main'>
            <Typography variant='h6' align='center' paragraph>
              {messages.viewAllCodesErrorTitle}
            </Typography>
            <Typography align='center'>{messages.viewAllCodesErrorDescription}</Typography>
          </Box>
        )}

        {showEmpty && (
          <EmptyDataTable
            title={messages.viewAllCodesEmptyTitle}
            description={messages.viewAllCodesEmptyDescription}
            sx={{ py: 6 }}
          />
        )}

        {showNoResults && (
          <EmptyDataTable
            title={messages.viewAllCodesNoResultsTitle}
            description={messages.viewAllCodesNoResultsDescription}
            sx={{ py: 6 }}
          />
        )}

        {codes.length > 0 && (
          <Box position='relative'>
            <Box sx={{ maxHeight: TABLE_MAX_HEIGHT, overflow: 'auto' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={stickyHeaderCellSx}>Promo Code</TableCell>
                    <TableCell sx={stickyHeaderCellSx}>Status</TableCell>
                    <TableCell sx={stickyHeaderCellSx}>Redeemed date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {codes.map((code) => (
                    <TableRow key={code.id}>
                      <TableCell>{code.code}</TableCell>
                      <TableCell>
                        <TrendBadge
                          color={getStatusColor(code.status)}
                          label={getStatusLabel(code.status)}
                        />
                      </TableCell>
                      <TableCell>
                        {code.redeemedAt
                          ? formatDateTime(parseDate(code.redeemedAt), DateTimeFormat.DateTime12h)
                          : ''}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            <Box display='flex' justifyContent='flex-end'>
              <Pagination {...paginationProps} />
            </Box>
            {isLoading && <LoadingOverlay rounded loaderSize={32} />}
          </Box>
        )}
      </TableContainer>
    </InfoDialog>
  );
};
